import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalyticsFilters } from '../../../../../contexts/AnalyticsFiltersContext';
import { getMockTopicDetail } from '../../../../../mocks/mockAnalyticsData';
import analyticsService from '../../../../../services/analyticsService';

type TopicDetailUser = {
  externalId: string;
  extendedSearchCount: number;
  frequency: number;
  lastName: string;
  name: string;
  userId: string;
};

type TopicDetailResponse = {
  endDate: string;
  startDate: string;
  topicId: string;
  users: TopicDetailUser[];
};

type UseTopicDetailProps = {
  courseIds: string[];
  endDate: string | undefined;
  isExpanded: boolean;
  startDate: string | undefined;
  topicId: string | null;
};

type UseTopicDetailReturn = {
  error: string | null;
  loading: boolean;
  users: TopicDetailUser[];
};

function useTopicDetail({ courseIds, endDate, isExpanded, startDate, topicId }: UseTopicDetailProps): UseTopicDetailReturn {
  const { i18n } = useTranslation();
  const { isMockMode } = useAnalyticsFilters();
  const [users, setUsers] = useState<TopicDetailUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const fetchDetail = useCallback(async () => {
    if (!topicId) return;

    setLoading(true);
    setError(null);

    if (isMockMode) {
      const mockDetails = getMockTopicDetail(i18n.language);
      setUsers(mockDetails[topicId] || []);
      setHasFetched(true);
      setLoading(false);
      return;
    }

    try {
      const data: TopicDetailResponse = await analyticsService.getTopicDetail({
        courseIds,
        startDate,
        endDate,
        topicId,
      });

      setUsers(data.users || []);
      setHasFetched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [courseIds, startDate, endDate, topicId, isMockMode, i18n.language]);

  useEffect(() => {
    if (isExpanded && !hasFetched) {
      fetchDetail();
    }
  }, [isExpanded, hasFetched, fetchDetail]);

  useEffect(() => {
    setHasFetched(false);
    setUsers([]);
  }, [courseIds, startDate, endDate]);

  return {
    error,
    loading,
    users,
  };
}

export default useTopicDetail;
export type { TopicDetailUser };
